/**
 * @fileoverview Client-side error enrichment for production observability.
 * @description Decodes minified React production errors, classifies hydration
 * mismatches, and builds stable fingerprints for deduplication in BetterStack.
 *
 * @module lib/observability/client-errors
 */

/**
 * THE single registry for React production error codes this app decodes and
 * classifies. Every consumer derives from this table — `HARD_*`/`RECOVERED_*`
 * sets, the decode text, the string family set that
 * `client-error-classification.ts` imports, and the shared minified-error
 * regex — so adding a code is a one-line change here, never a parallel edit
 * held in lockstep by comment.
 *
 * Placement semantics:
 *  - `hard`        — alert-worthy hydration mismatch → `client_hydration_error`
 *  - `recovered`   — React recovered by client rendering → `client_hydration_recovered`
 *  - `decode-only` — decoded for readability, NOT a hydration signal
 *
 * "Emitted" below = present in the installed react-dom 19.2.6 client
 * production bundle (`formatProdErrorMessage(<code>` sites, multiline-safe
 * scan); legacy codes can still reach `/api/log` from stale deployed bundles
 * during deploy skew, so they are classified too.
 *
 * | Code | Emitted by 19.2.6 | Placement    | Why |
 * |------|-------------------|--------------|-----|
 * | 418  | yes               | hard         | hydration mismatch (HTML/text arg) |
 * | 419  | yes               | recovered    | server Suspense failure → client render |
 * | 421  | no (legacy)       | recovered    | update before boundary hydrated → client render |
 * | 422  | yes               | recovered    | recovered via nearest Suspense boundary |
 * | 423  | yes               | recovered    | recovered by client rendering entire root |
 * | 424  | yes               | recovered    | early root update → root client render |
 * | 425  | no (legacy)       | hard         | React-18-era text mismatch (folded into #418 in 19.x) |
 * | 426  | no (legacy)       | decode-only  | sync-input suspension — Suspense UX, not hydration |
 */
export type ReactErrorPlacement = 'hard' | 'recovered' | 'decode-only'

export const REACT_ERROR_REGISTRY: Readonly<
  Record<number, { placement: ReactErrorPlacement; text: string }>
> = {
  418: {
    placement: 'hard',
    text: 'Hydration failed because the server rendered HTML did not match the client.',
  },
  419: {
    placement: 'recovered',
    text: 'The server could not finish this Suspense boundary, likely due to an error during server rendering. Switched to client rendering.',
  },
  421: {
    placement: 'recovered',
    text: 'This Suspense boundary received an update before it finished hydrating. This caused the boundary to switch to client rendering.',
  },
  422: {
    placement: 'recovered',
    text: 'There was an error while hydrating but React was able to recover by instead client rendering from the nearest Suspense boundary.',
  },
  423: {
    placement: 'recovered',
    text: 'There was an error while hydrating but React was able to recover by instead client rendering the entire root.',
  },
  424: {
    placement: 'recovered',
    text: 'This root received an early update, before anything was able hydrate. Switched the entire root to client rendering.',
  },
  425: {
    placement: 'hard',
    text: 'Text content does not match server-rendered HTML.',
  },
  426: {
    placement: 'decode-only',
    text: 'A component suspended while responding to synchronous input.',
  },
}

/**
 * The one minified-React-error matcher. Shared (also by
 * `client-error-classification.ts`) so message parsing can never drift
 * between the classifier and the enrichment. No `g` flag — `exec` is
 * stateless and safe to share.
 */
export const REACT_MINIFIED_ERROR_RE = /Minified React error #(\d+)/i

function codesWithPlacement(placement: ReactErrorPlacement): ReadonlySet<number> {
  return new Set(
    Object.entries(REACT_ERROR_REGISTRY)
      .filter(([, entry]) => entry.placement === placement)
      .map(([code]) => Number(code)),
  )
}

/** React codes that represent hard hydration failures (alert-worthy). */
const HARD_HYDRATION_REACT_CODES = codesWithPlacement('hard')

/** React codes where hydration recovered by client rendering. */
const RECOVERED_HYDRATION_REACT_CODES = codesWithPlacement('recovered')

/**
 * The hydration FAMILY (hard + recovered) as strings — the shape
 * `client-error-classification.ts` emits in its `reactErrorCode` field.
 * Derived, so the classifier's `isHydrationError` flag can never drift from
 * this registry's placements.
 */
export const REACT_HYDRATION_ERROR_CODE_STRINGS: ReadonlySet<string> = new Set(
  Object.entries(REACT_ERROR_REGISTRY)
    .filter(([, entry]) => entry.placement !== 'decode-only')
    .map(([code]) => code),
)

/** Error categories emitted to BetterStack for alert routing. */
export type ClientErrorCategory =
  | 'hydration_mismatch'
  | 'hydration_recovered'
  | 'error_boundary'
  | 'react_runtime'
  | 'runtime_error'
  | 'unhandled_rejection'
  | 'unknown'

/** Structured event names routed through `/api/log`. */
export type ClientErrorEvent =
  | 'client_hydration_error'
  | 'client_hydration_recovered'
  | 'client_error'

/**
 * Structured diagnostics attached to client error logs.
 *
 * @typedef {Object} ClientErrorDiagnostics
 * @property {ClientErrorCategory} errorCategory - High-level bucket for dashboards.
 * @property {ClientErrorEvent} clientEvent - Structured event name for `/api/log`.
 * @property {LogLevel} logLevel - Severity routed on the server.
 * @property {boolean} [isHydrationMismatch] - True for hard hydration failures (#418/#425).
 * @property {boolean} [isHydrationRecovered] - True when React recovered (#422/#423).
 * @property {number} [reactErrorCode] - Decoded React minified error code, when present.
 * @property {string} [reactErrorDecoded] - Human-readable React error text.
 * @property {string} [errorFingerprint] - Stable key for per-session deduplication.
 * @property {string} [pathname] - Page path without query string.
 * @property {string} [pageUrl] - Full page URL when available.
 */
export interface ClientErrorDiagnostics {
  errorCategory: ClientErrorCategory
  clientEvent: ClientErrorEvent
  logLevel: 'error' | 'warn'
  isHydrationMismatch?: boolean
  isHydrationRecovered?: boolean
  reactErrorCode?: number
  reactErrorDecoded?: string
  errorFingerprint?: string
  pathname?: string
  pageUrl?: string
}

type LogLevel = ClientErrorDiagnostics['logLevel']

/**
 * Extracts the React minified error code from a message, if present.
 *
 * @param {string} message - Raw error message.
 * @returns {number | undefined} Parsed React error code.
 */
export function parseReactMinifiedErrorCode(message: string): number | undefined {
  const match = REACT_MINIFIED_ERROR_RE.exec(message)
  if (!match?.[1]) {
    return undefined
  }
  const code = Number.parseInt(match[1], 10)
  return Number.isFinite(code) ? code : undefined
}

/**
 * Decodes a React minified production error message when possible.
 *
 * @param {string} message - Raw error message from `window.onerror` or an Error.
 * @returns {string | undefined} Decoded explanation, if the code is known.
 */
export function decodeReactMinifiedError(message: string): string | undefined {
  const code = parseReactMinifiedErrorCode(message)
  if (code === undefined) {
    return undefined
  }
  return REACT_ERROR_REGISTRY[code]?.text ?? `React minified error #${code}`
}

/**
 * Classifies whether an error message represents a hard hydration mismatch.
 *
 * @param {string} message - Raw error message.
 * @param {number} [reactErrorCode] - Optional pre-parsed React error code.
 * @returns {boolean} True when the error is an alert-worthy hydration failure.
 */
export function isHydrationMismatchMessage(
  message: string,
  reactErrorCode?: number,
): boolean {
  if (reactErrorCode !== undefined && HARD_HYDRATION_REACT_CODES.has(reactErrorCode)) {
    return true
  }
  return /hydration failed/i.test(message)
}

/**
 * Classifies whether React recovered from a hydration error (#422/#423).
 *
 * @param {string} message - Raw error message.
 * @param {number} [reactErrorCode] - Optional pre-parsed React error code.
 * @returns {boolean} True when React recovered via client render or Suspense boundary.
 */
export function isHydrationRecoveredMessage(
  message: string,
  reactErrorCode?: number,
): boolean {
  if (reactErrorCode !== undefined && RECOVERED_HYDRATION_REACT_CODES.has(reactErrorCode)) {
    return true
  }
  return /recover by instead client rendering/i.test(message)
}

/**
 * Builds a stable fingerprint for deduplicating repeated client errors in a session.
 *
 * @param {Object} input - Fingerprint inputs.
 * @param {string} input.message - Error message.
 * @param {string} [input.pathname] - Current pathname.
 * @param {string} [input.type] - Error type label from the log provider.
 * @param {number} [input.reactErrorCode] - Decoded React error code.
 * @param {string} [input.filename] - Script URL from `window.onerror`.
 * @param {number} [input.lineno] - Line number from `window.onerror`.
 * @param {number} [input.colno] - Column number from `window.onerror`.
 * @param {string} [input.stack] - Error stack trace.
 * @returns {string} Fingerprint string.
 */
export function buildClientErrorFingerprint(input: {
  message: string
  pathname?: string
  type?: string
  reactErrorCode?: number
  filename?: string
  lineno?: number
  colno?: number
  stack?: string
}): string {
  const stackFrame = input.stack?.split('\n')[1]?.trim().slice(0, 120) ?? ''
  const location = input.filename
    ? `${input.filename}:${input.lineno ?? 0}:${input.colno ?? 0}`
    : stackFrame || 'no-location'

  const parts = [
    input.type ?? 'runtime_error',
    input.pathname ?? 'unknown-path',
    input.reactErrorCode !== undefined ? `react-${input.reactErrorCode}` : 'no-react-code',
    location,
    input.message.slice(0, 180),
  ]
  return parts.join('|')
}

/**
 * Resolves the structured event and severity for a client error.
 *
 * @param {Object} input - Classification inputs.
 * @param {boolean} input.hydrationMismatch - Hard hydration failure flag.
 * @param {boolean} input.hydrationRecovered - Recovered hydration flag.
 * @returns {{ clientEvent: ClientErrorEvent, logLevel: LogLevel }} Routing metadata.
 */
export function resolveClientErrorEvent(input: {
  hydrationMismatch: boolean
  hydrationRecovered: boolean
}): { clientEvent: ClientErrorEvent; logLevel: LogLevel } {
  if (input.hydrationMismatch) {
    return { clientEvent: 'client_hydration_error', logLevel: 'error' }
  }
  if (input.hydrationRecovered) {
    return { clientEvent: 'client_hydration_recovered', logLevel: 'warn' }
  }
  return { clientEvent: 'client_error', logLevel: 'error' }
}

/**
 * Enriches a client error log context with decoded React metadata and routing fields.
 *
 * @param {Error | string} error - Error instance or message string.
 * @param {Record<string, unknown>} context - Existing log context from the caller.
 * @returns {ClientErrorDiagnostics & Record<string, unknown>} Merged diagnostics payload.
 */
export function enrichClientErrorContext(
  error: Error | string,
  context: Record<string, unknown> = {},
): ClientErrorDiagnostics & Record<string, unknown> {
  const message = error instanceof Error ? error.message : String(error)
  const reactErrorCode = parseReactMinifiedErrorCode(message)
  const reactErrorDecoded = decodeReactMinifiedError(message)
  const hydrationRecovered = isHydrationRecoveredMessage(message, reactErrorCode)
  const hydrationMismatch = isHydrationMismatchMessage(message, reactErrorCode)

  const pageUrl =
    typeof context.url === 'string'
      ? context.url
      : typeof window !== 'undefined'
        ? window.location.href
        : undefined

  let pathname: string | undefined
  if (pageUrl) {
    try {
      pathname = new URL(pageUrl).pathname
    } catch {
      pathname = undefined
    }
  }

  const type =
    typeof context.type === 'string' ? context.type : undefined

  const errorCategory: ClientErrorCategory = hydrationMismatch
    ? 'hydration_mismatch'
    : hydrationRecovered
      ? 'hydration_recovered'
      : type === 'error_boundary'
        ? 'error_boundary'
        : type === 'unhandled_rejection'
          ? 'unhandled_rejection'
          : reactErrorCode !== undefined
            ? 'react_runtime'
            : type === 'runtime_error'
              ? 'runtime_error'
              : 'unknown'

  const { clientEvent, logLevel } = resolveClientErrorEvent({
    hydrationMismatch,
    hydrationRecovered,
  })

  const filename = typeof context.filename === 'string' ? context.filename : undefined
  const lineno = typeof context.lineno === 'number' ? context.lineno : undefined
  const colno = typeof context.colno === 'number' ? context.colno : undefined
  const stack = error instanceof Error ? error.stack : undefined

  const errorFingerprint = buildClientErrorFingerprint({
    message,
    pathname,
    type,
    reactErrorCode,
    filename,
    lineno,
    colno,
    stack,
  })

  return {
    ...context,
    errorCategory,
    clientEvent,
    logLevel,
    ...(hydrationMismatch ? { isHydrationMismatch: true } : {}),
    ...(hydrationRecovered ? { isHydrationRecovered: true } : {}),
    ...(reactErrorCode !== undefined ? { reactErrorCode } : {}),
    ...(reactErrorDecoded ? { reactErrorDecoded } : {}),
    ...(pathname ? { pathname } : {}),
    ...(pageUrl ? { pageUrl } : {}),
    errorFingerprint,
  }
}
